import EditableField from '@wandb/common/components/EditableField';
import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import {SingleLineText} from '@wandb/common/components/elements/Text';
import HighlightedIcon from '@wandb/common/components/HighlightedIcon';
import {PopupDropdown} from '@wandb/common/components/PopupDropdown';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import * as queryString from 'query-string';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
import {Popup} from 'semantic-ui-react';

import * as Generated from '../generated/graphql';
import {auth, isInIframe} from '../setup';
import {useQuery} from '../state/graphql/query';
import {usePropsSelector, useSelector} from '../state/hooks';
import {PanelBankUpdaterContext} from '../state/panelbank/context';
import {useReportCommentAlerts} from '../state/reports/hooks';
import * as ReportViewsSelectors from '../state/reports/selectors';
import {ReportViewRef} from '../state/reports/types';
import * as ReportTypes from '../state/reports/types';
import * as ViewerHooks from '../state/viewer/hooks';
import * as ViewActions from '../state/views/actions';
import {ViewPermissionsData} from '../state/views/graphql';
import {usePart, useViewRefAction} from '../state/views/hooks';
import * as ViewHooks from '../state/views/hooks';
import {ReportPageNormalized} from '../state/views/report/types';
import * as ViewSelectors from '../state/views/selectors';
import * as ViewThunks from '../state/views/thunks';
import {isOwnView} from '../state/views/util';
import {useAdminModeActive, viewingAs} from '../util/admin';
import {scrollToElement} from '../util/document';
import {useIsGalleryReport, useTrackReportActivity} from '../util/gallery';
import * as Urls from '../util/urls';
import CloneReportModal from './CloneReportModal';
import EditReportTrigger from './EditReportTrigger';
import {PersistentFlasher} from './elements/PersistentFlasher';
import {ReportDiscussionUpdaterContext} from './ReportDiscussionContext';
import ReportExportModal from './ReportExportModal';
import * as S from './ReportPageHeaderView.styles';
import {ShareReportTrigger} from './ShareReportTrigger';
import StargazerModal from './StargazerModal';

interface ReportPageHeaderViewProps {
  viewRef: ReportViewRef;
  editAccess?: boolean;
  fullWidth?: boolean;
}

export default (props: ReportPageHeaderViewProps) => {
  const {setCommentFrame} = useContext(ReportDiscussionUpdaterContext);

  const {viewRef, fullWidth} = props;

  const admin = useAdminModeActive();

  const viewer = ViewerHooks.useViewer();
  const view = useSelector(state => state.views.views[viewRef.id]);
  const reportID = view.id;
  if (reportID == null || view.partRef == null) {
    throw new Error('invalid state');
  }

  const {isGalleryReport} = useIsGalleryReport(reportID);

  const [viewView] = Generated.useViewViewMutation();
  const accessQuery = useQuery<
    ViewPermissionsData,
    Generated.ViewPermissionsQueryVariables
  >(Generated.ViewPermissionsDocument, {
    variables: {
      projectName: view.project?.name ?? '',
      entityName: view.project?.entityName ?? '',
    },
    skip: view.project == null,
  });
  React.useEffect(() => {
    // viewView will fail with permission error if in impersonating mode
    if (viewingAs() !== '') {
      return;
    }
    viewView({variables: {id: reportID}});
  }, [reportID, viewView]);
  const isPublic = accessQuery.loading
    ? false
    : ['USER_READ', 'USER_WRITE'].includes(accessQuery.project?.access);
  const viewPart = usePart(view.partRef) as ReportPageNormalized;
  const setNotes = useViewRefAction(viewRef, ViewActions.setDescription);
  const entityName = view.project?.entityName ?? view.entityName;
  const entityQuery = Generated.useEntityQuery({
    variables: {
      name: entityName,
    },
    skip: entityName == null,
  });
  const userIsTeamAdmin = useMemo(
    () =>
      entityQuery.loading
        ? false
        : entityQuery.data?.entity?.members.some(
            member => member.id === viewer?.id && member.admin
          ),
    [entityQuery, viewer]
  );
  const userIsMemberOfTeam = useMemo(
    () =>
      entityQuery.loading
        ? false
        : entityQuery.data?.entity?.members.some(
            member => member.id === viewer?.id
          ),
    [entityQuery, viewer]
  );
  const projectName = view.project?.name;
  const viewAuthors = viewPart.authors;
  let authors;
  if (viewAuthors != null && viewAuthors.length > 0) {
    authors = viewAuthors;
  } else {
    authors = [_.pick(view.user, 'username', 'name')];
  }
  const notes = view.description || '';

  const track = useTrackReportActivity(reportID);

  const starCount = usePropsSelector(
    ViewSelectors.makeStarCountSelector,
    viewRef
  );
  const starred = usePropsSelector(ViewSelectors.makeStarredSelector, viewRef);
  const star = ViewHooks.useViewRefThunk(viewRef, ViewThunks.starView);
  const unstar = ViewHooks.useViewRefThunk(viewRef, ViewThunks.unstarView);
  const toggleStar = useCallback(() => {
    if (viewer == null) {
      auth.login();
      return;
    }
    if (starred) {
      track('unheartReport', reportID);
      unstar();
    } else {
      track('heartReport', reportID);
      star();
    }
  }, [viewer, starred, star, unstar, reportID, track]);

  const [stargazerModalOpen, setStargazerModalOpen] = useState(false);
  const openStargazerModal = useCallback(() => setStargazerModalOpen(true), []);
  const closeStargazerModal = useCallback(
    () => setStargazerModalOpen(false),
    []
  );

  const initHighlightShare = useMemo(() => {
    const qs = queryString.parse(window.location.search);
    return qs.highlightShare !== undefined;
  }, []);
  const [highlightShare, setHighlightShare] = useState(initHighlightShare);
  const dismissHighlightShare = useCallback(() => {
    if (highlightShare) {
      window.analytics?.track('Flasher Dismissed', {
        entityName,
        projectName,
        reportID,
        location: 'share report button',
      });
    }
    setHighlightShare(false);
    // eslint-disable-next-line
  }, [entityName, projectName, reportID]);

  useEffect(() => {
    if (highlightShare) {
      window.analytics?.track('Flasher Viewed', {
        entityName,
        projectName,
        reportID,
        location: 'share report button',
      });
    }
  }, [highlightShare, entityName, projectName, reportID]);

  const [cloneReportModalOpen, setCloneReportModalOpen] = useState(false);
  const openCloneReportModal = useCallback(
    () => setCloneReportModalOpen(true),
    []
  );
  const closeCloneReportModal = useCallback(
    () => setCloneReportModalOpen(false),
    []
  );
  const [moveReportModalOpen, setMoveReportModalOpen] = useState(false);
  const openMoveReportModal = useCallback(
    () => setMoveReportModalOpen(true),
    []
  );
  const closeMoveReportModal = useCallback(
    () => setMoveReportModalOpen(false),
    []
  );

  const [reportExportModalOpen, setReportExportModalOpen] = useState(false);
  const {setDisableWaitToLoad} = React.useContext(PanelBankUpdaterContext);

  const {isSubscribed, subscribe, unsubscribe} =
    useReportCommentAlerts(viewRef);
  const commentsCount = useSelector(
    ReportViewsSelectors.getReportDiscussionCommentsCount(viewRef)
  );

  const ownView = isOwnView(viewer, view);

  const cloneDropdownOption =
    viewer != null && projectName != null && !isInIframe()
      ? {
          text: 'Clone this report',
          icon: 'wbic-ic-copy',
          onClick: openCloneReportModal,
        }
      : null;
  const moveDropdownOption =
    viewer != null &&
    projectName != null &&
    admin &&
    view.type === ReportTypes.REPORT_VIEW_TYPE
      ? {
          text: 'Move this report',
          icon: 'wbic-ic-copy',
          onClick: openMoveReportModal,
        }
      : null;
  const exportDropdownOption =
    projectName != null
      ? {
          text: 'Download',
          icon: 'wbic-ic-download',
          onClick: () => {
            setReportExportModalOpen(true);
            setDisableWaitToLoad(true);
          },
        }
      : null;
  const subscribeDropdownOption =
    viewer != null
      ? {
          text: `Turn ${isSubscribed ? 'off' : 'on'} notifications`,
          icon: `wbic-ic-${
            isSubscribed ? 'bell-outline' : 'bell-slash-outline'
          }`,
          onClick: () => (isSubscribed ? unsubscribe() : subscribe()),
          'data-test': 'report-header-subscribe',
        }
      : null;
  const commentDropdownOption = {
    text: `${commentsCount} • View comments`,
    icon: 'wbic-ic-chat',
    onClick: () => scrollToElement('.report-discussion'),
    'data-test': 'report-header-view-comments',
  };
  const heartsDropdownOption = {
    text: `${starCount} • View likes`,
    icon: 'wbic-ic-heart-outline',
    onClick: openStargazerModal,
  };
  const viewsDropdownOption = {
    text: `${view.viewCount} page views`,
    icon: 'wbic-ic-show',
    style: {
      cursor: 'default',
      background: 'transparent',
      opacity: 0.5,
    },
  };

  const notOwnViewDropdownOptions = _.compact([
    cloneDropdownOption,
    moveDropdownOption,
    exportDropdownOption,
    subscribeDropdownOption,
  ]);
  const ownViewDropdownOptions =
    ownView || admin
      ? [commentDropdownOption, heartsDropdownOption, viewsDropdownOption]
      : null;
  const dropdownOptions = _.compact([
    notOwnViewDropdownOptions,
    ownViewDropdownOptions,
  ]);

  const hasEditAccess =
    view.type === ReportTypes.REPORT_VIEW_TYPE &&
    props.editAccess &&
    (ownView || !view.locked || admin);

  const userOwnsReport = ownView;

  const userCanShareReport =
    userOwnsReport || userIsMemberOfTeam || admin || isPublic;

  return (
    <div className={`report-header-view`}>
      {hasEditAccess ? (
        <S.EditorButtonSection>
          <EditReportTrigger viewRef={viewRef}>
            <S.ButtonWrapper>
              <S.ButtonIcon name="edit" />
              Edit
            </S.ButtonWrapper>
          </EditReportTrigger>
          <ShareReportTrigger
            viewRef={viewRef}
            userIsMemberOfTeam={userIsMemberOfTeam}
            userIsTeamAdmin={userIsTeamAdmin}
            onTriggerContentClick={dismissHighlightShare}>
            <PersistentFlasher
              visible={highlightShare}
              offsetX={-1}
              offsetY={15}
              style={{zIndex: 99}}
              trigger={
                <S.ButtonWrapper>
                  <S.ButtonIcon name="share" />
                  Share
                </S.ButtonWrapper>
              }
            />
          </ShareReportTrigger>
        </S.EditorButtonSection>
      ) : (
        <S.EmptyEditorButtonSection />
      )}
      {view.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE && (
        // Since reports are now WYSIWYG, preview mode is obsolete,
        // so this state should not be possible to get to via the UI.
        // I'm leaving a little message here in case someone somehow gets to
        // this type of URL.
        <div className="report-header-view__draft-preview-message">
          <div className="report-header-view__draft-preview-message-info">
            You are currently previewing a{' '}
            {view.parentId ? 'draft edit of a report' : 'draft of a new report'}
            . This draft is private to {ownView ? 'you' : view.user.username}.
            Report previews are a deprecated feature; if you arrived here
            accidentally, let us know how.
          </div>
        </div>
      )}
      <div
        className={classNames(`report-header-view__content`, {
          'full-width': fullWidth,
        })}>
        <div className="report-header-view__title">
          <div className="report-header-view__title-name">
            <SingleLineText as="h1">{view.displayName}</SingleLineText>
            {notes !== '' && (
              <div className="report-header-view__description">
                <EditableField
                  multiline
                  maxLength={512}
                  readOnly={true}
                  placeholder={'Add a description...'}
                  value={notes}
                  // Report metadata is loaded asynchronously so we need to support
                  // dynamic updates to the description.
                  updateValue={true}
                  save={setNotes}
                />
              </div>
            )}
            <div className="report-header-view__meta">
              <div className="report-header-view__authors">
                {authors.map(({username, name}, i) => (
                  <React.Fragment key={username}>
                    <Link
                      className="report-header-view__author"
                      to={Urls.profilePage(username)}>
                      {name || username}
                    </Link>
                    {i < authors.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </div>
              <div className="report-header-view__actions">
                {view.type === ReportTypes.REPORT_VIEW_TYPE && (
                  <>
                    <Popup
                      inverted
                      position="top center"
                      content={
                        commentsCount > 0
                          ? `${commentsCount} comments`
                          : `Add a comment`
                      }
                      size="mini"
                      on="hover"
                      trigger={
                        <div
                          className="report-header-view__action"
                          onClick={() => {
                            setCommentFrame('editor');
                          }}>
                          <LegacyWBIcon name="chat" />
                        </div>
                      }
                    />
                    {userCanShareReport ? (
                      <ShareReportTrigger
                        userIsMemberOfTeam={userIsMemberOfTeam}
                        userIsTeamAdmin={userIsTeamAdmin}
                        viewRef={viewRef}
                        onTriggerContentClick={dismissHighlightShare}>
                        <PersistentFlasher
                          visible={highlightShare}
                          offsetX={-1}
                          offsetY={15}
                          style={{zIndex: 99}}
                          trigger={
                            <Popup
                              inverted
                              position="top center"
                              content="Share"
                              size="mini"
                              on="hover"
                              trigger={
                                <div className="report-header-view__action">
                                  <LegacyWBIcon name="share" />
                                </div>
                              }
                            />
                          }
                        />
                      </ShareReportTrigger>
                    ) : (
                      <Popup
                        inverted
                        position="top center"
                        content="Sharing unavailable for view-only access"
                        size="mini"
                        on="hover"
                        trigger={
                          <div className="report-header-view__action">
                            <LegacyWBIcon name="share" />
                          </div>
                        }
                      />
                    )}
                    <Popup
                      inverted
                      position="top center"
                      content={`${starCount} hearts`}
                      size="mini"
                      on="hover"
                      trigger={
                        <div
                          className="report-header-view__action"
                          onClick={toggleStar}>
                          <LegacyWBIcon
                            name={starred ? 'heart' : 'heart-outline'}
                          />
                        </div>
                      }
                    />
                    <PopupDropdown
                      data-test="report-header-actions-dropdown"
                      offset={'10px, -10px'}
                      position="bottom right"
                      trigger={
                        <div
                          data-test="report-header-actions-overflow"
                          className="report-header-view__action">
                          <HighlightedIcon>
                            <LegacyWBIcon name="overflow" />
                          </HighlightedIcon>
                        </div>
                      }
                      sections={dropdownOptions}
                    />
                    {cloneReportModalOpen && (
                      <CloneReportModal
                        viewRef={viewRef}
                        onClose={closeCloneReportModal}
                      />
                    )}
                    {moveReportModalOpen && (
                      <CloneReportModal
                        moveReport
                        viewRef={viewRef}
                        onClose={closeMoveReportModal}
                      />
                    )}
                    {projectName != null && (
                      <ReportExportModal
                        entityName={entityName}
                        projectName={projectName}
                        reportViewRef={viewRef}
                        open={reportExportModalOpen}
                        onClose={() => setReportExportModalOpen(false)}
                      />
                    )}
                    <StargazerModal
                      viewRef={viewRef}
                      open={stargazerModalOpen}
                      onClose={closeStargazerModal}
                    />
                  </>
                )}
              </div>
            </div>
            {isGalleryReport && (
              <S.LastUpdated>
                Last Updated: {moment(view.updatedAt).format('MMM D, YYYY')}
              </S.LastUpdated>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
